import type { RouteRecordName } from 'vue-router';
import { type MenuSection } from '@/routers-capability/routeMeta';
import { type LayoutVariant } from '@/utils/composables/useLayoutVariant';

/**
 * 旧デザイン（/others）と新デザイン（/settings・/admin）で同じ画面が別ルートとして定義されている。
 * ルート名を直接書かず、この表と resolveOrganizationTagVariant 経由で解決する。
 * 参照側はコンポーネントなら useOrganizationTagRoute を使う。
 */
export type OrganizationTagVariant = Extract<
  MenuSection,
  'others' | 'admin' | 'settings'
>;

export const ORGANIZATION_TAG_ROUTE_NAMES = {
  others: { list: 'organizationTags', detail: 'organizationTagDetail' },
  admin: {
    list: 'adminOrganizationTags',
    detail: 'adminOrganizationTagDetail',
  },
  settings: {
    list: 'settingsOrganizationTags',
    detail: 'settingsOrganizationTagDetail',
  },
} as const satisfies Record<
  OrganizationTagVariant,
  { list: string; detail: string }
>;

/** 旧デザインは /others/* のみ。新デザインは今いるセクション、セクション外なら権限で決める */
export const resolveOrganizationTagVariant = ({
  isNextLayout,
  section,
  isAdminUser,
}: {
  isNextLayout: boolean;
  section: MenuSection | undefined;
  isAdminUser: boolean;
}): OrganizationTagVariant => {
  if (!isNextLayout) return 'others';
  if (section === 'admin' || section === 'settings') return section;
  return isAdminUser ? 'admin' : 'settings';
};

const counterpartsFor = (target: LayoutVariant): Record<string, string> => {
  const { others, admin, settings } = ORGANIZATION_TAG_ROUTE_NAMES;
  const to = target === 'legacy' ? others : settings;
  const from = target === 'legacy' ? [admin, settings] : [others];

  return Object.fromEntries(
    from.flatMap(names => [
      [names.list, to.list],
      [names.detail, to.detail],
    ]),
  );
};

const COUNTERPART_ROUTE_NAMES: Record<LayoutVariant, Record<string, string>> = {
  legacy: counterpartsFor('legacy'),
  next: counterpartsFor('next'),
};

/**
 * 切り替え先のデザインで同じ画面にあたるルート名。対向がなければ undefined。
 * ナビゲーションガードから呼ぶため権限は見ない（userInfo が未取得なことがある）。
 * 管理者は管理メニューから admin バリアントに直接入るので実害はない。
 */
export const counterpartRouteName = (
  name: RouteRecordName | null | undefined,
  target: LayoutVariant,
): string | undefined =>
  typeof name === 'string' ? COUNTERPART_ROUTE_NAMES[target][name] : undefined;
